// Procedural building geometry generated from surveyed footprints.
// Every builder takes the footprint as [[x, z], ...] and returns BufferGeometries per material slot.
import * as THREE from 'three/webgpu';

/** Accumulates flat-shaded triangles for one material slot. */
class Mesher {
  constructor() { this.p = []; this.uv = []; }
  tri(a, b, c, uva = [0, 0], uvb = [1, 0], uvc = [1, 1]) {
    this.p.push(...a, ...b, ...c);
    this.uv.push(...uva, ...uvb, ...uvc);
  }
  quad(a, b, c, d, u0 = 0, u1 = 1, v0 = 0, v1 = 1) {
    this.tri(a, b, c, [u0, v0], [u1, v0], [u1, v1]);
    this.tri(a, c, d, [u0, v0], [u1, v1], [u0, v1]);
  }
  geometry() {
    if (!this.p.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.p, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.computeVertexNormals();
    return g;
  }
}

const signedArea = (pts) => {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, z1] = pts[i], [x2, z2] = pts[(i + 1) % pts.length];
    a += x1 * z2 - x2 * z1;
  }
  return a / 2;
};

/** Faces built edge-by-edge point outwards when the ring has negative signed area in (x, z). */
export function orient(pts) {
  return signedArea(pts) > 0 ? [...pts].reverse() : pts;
}

export function centroid(pts) {
  let x = 0, z = 0;
  for (const p of pts) { x += p[0]; z += p[1]; }
  return [x / pts.length, z / pts.length];
}

/** Minimum-area oriented bounding box: axis u is the long side. */
export function obb(pts) {
  let best = null;
  for (let i = 0; i < pts.length; i++) {
    const [x1, z1] = pts[i], [x2, z2] = pts[(i + 1) % pts.length];
    const len = Math.hypot(x2 - x1, z2 - z1);
    if (len < 0.2) continue;
    const ux = (x2 - x1) / len, uz = (z2 - z1) / len;
    let umin = Infinity, umax = -Infinity, wmin = Infinity, wmax = -Infinity;
    for (const [x, z] of pts) {
      const u = x * ux + z * uz, w = -x * uz + z * ux;
      umin = Math.min(umin, u); umax = Math.max(umax, u);
      wmin = Math.min(wmin, w); wmax = Math.max(wmax, w);
    }
    const area = (umax - umin) * (wmax - wmin);
    if (!best || area < best.area) best = { area, ux, uz, umin, umax, wmin, wmax };
  }
  // Make u the long axis.
  if (best.wmax - best.wmin > best.umax - best.umin) {
    const { ux, uz, umin, umax, wmin, wmax } = best;
    best = { area: best.area, ux: -uz, uz: ux, umin: wmin, umax: wmax, wmin: -umax, wmax: -umin };
  }
  best.L = best.umax - best.umin;
  best.W = best.wmax - best.wmin;
  best.toUW = ([x, z]) => [x * best.ux + z * best.uz, -x * best.uz + z * best.ux];
  best.toXZ = (u, w) => [u * best.ux - w * best.uz, u * best.uz + w * best.ux];
  return best;
}

function walls(m, pts, y0, y1, uScale = 1) {
  let run = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, z1] = pts[i], [x2, z2] = pts[(i + 1) % pts.length];
    const len = Math.hypot(x2 - x1, z2 - z1);
    m.quad([x1, y0, z1], [x2, y0, z2], [x2, y1, z2], [x1, y1, z1], run / uScale, (run + len) / uScale, 0, (y1 - y0) / uScale);
    run += len;
  }
}

function cap(m, pts, y, up = true) {
  const v2 = pts.map(([x, z]) => new THREE.Vector2(x, z));
  for (const [a, b, c] of THREE.ShapeUtils.triangulateShape(v2, [])) {
    const A = [pts[a][0], y, pts[a][1]], B = [pts[b][0], y, pts[b][1]], C = [pts[c][0], y, pts[c][1]];
    // Normal y component of (B-A)x(C-A) is dz1*dx2 - dx1*dz2.
    const ny = (B[2] - A[2]) * (C[0] - A[0]) - (B[0] - A[0]) * (C[2] - A[2]);
    if ((ny > 0) === up) m.tri(A, B, C); else m.tri(A, C, B);
  }
}

/** Aurora Tower: octagonal walls, truncated pyramid roof, glass skylight at the apex. */
export function tower(ptsIn, { wallH = 2.6, roofH = 4.6, suite = false } = {}) {
  const pts = orient(ptsIn);
  const [cx, cz] = centroid(pts);
  const k = 0.26;
  const wall = new Mesher(), roof = new Mesher(), glass = new Mesher();
  walls(wall, pts, -0.8, wallH);
  const top = pts.map(([x, z]) => [cx + (x - cx) * k, cz + (z - cz) * k]);
  // Tower suites have glass panels in two opposite roof faces (the two glass-roof bedrooms).
  const lens = pts.map((p, i) => Math.hypot(pts[(i + 1) % pts.length][0] - p[0], pts[(i + 1) % pts.length][1] - p[1]));
  const longest = lens.indexOf(Math.max(...lens));
  const glassFaces = suite ? new Set([longest, (longest + Math.round(pts.length / 2)) % pts.length]) : new Set();
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const A = [pts[i][0], wallH, pts[i][1]], B = [pts[j][0], wallH, pts[j][1]];
    const C = [top[j][0], wallH + roofH, top[j][1]], D = [top[i][0], wallH + roofH, top[i][1]];
    if (glassFaces.has(i)) {
      // Lower band roof, upper part glass.
      const mid = (P, Q, t) => P.map((v, n) => v + (Q[n] - v) * t);
      const A2 = mid(A, D, 0.3), B2 = mid(B, C, 0.3), C2 = mid(B, C, 0.85), D2 = mid(A, D, 0.85);
      roof.quad(A, B, B2, A2); glass.quad(A2, B2, C2, D2); roof.quad(D2, C2, C, D);
    } else roof.quad(A, B, C, D, 0, lens[i] / 2);
  }
  // Skylight: short glass collar plus a glass cap.
  const ringTop = wallH + roofH, skyH = 0.45;
  walls(glass, top, ringTop, ringTop + skyH);
  cap(glass, top, ringTop + skyH, true);
  return { wall: wall.geometry(), roof: roof.geometry(), glass: glass.geometry(), top: ringTop + skyH };
}

/** Complex outlines: hip roof from an inset ring (precomputed with Shapely), via a bevelled extrusion. */
export function hipBuilding(ptsIn, inset, insetD, { wallH = 4.5, roofH = 3.2, bandFrom = 0.9, bandTo = 2.6 } = {}) {
  const pts = orient(ptsIn);
  const wall = new Mesher(), band = new Mesher();
  walls(wall, pts, -0.8, wallH, 2);
  // Window band, pushed out a few centimetres so it draws over the wall.
  const [cx, cz] = centroid(pts);
  const out = pts.map(([x, z]) => {
    const dx = x - cx, dz = z - cz, d = Math.hypot(dx, dz) || 1;
    return [x + (dx / d) * 0.06, z + (dz / d) * 0.06];
  });
  walls(band, out, bandFrom, bandTo, 3);
  const shape = new THREE.Shape(orient(inset).map(([x, z]) => new THREE.Vector2(x, -z)));
  const roof = new THREE.ExtrudeGeometry(shape, {
    depth: 0.01, bevelEnabled: true, bevelThickness: roofH, bevelSize: insetD + 0.6, bevelSegments: 1, steps: 1,
  });
  roof.rotateX(-Math.PI / 2);
  roof.translate(0, wallH, 0);
  return { wall: wall.geometry(), roof: roof.toNonIndexed(), band: band.geometry(), top: wallH + roofH };
}
